import React, { useState, useEffect, useCallback } from 'react';
import { getSentiment } from '../../services/SentimentService';
import { useIntl } from 'react-intl';

// Module-level cache: fetched ONCE on first visit and kept across navigation.
// Refreshes only on the button click (or a live WebSocket push).
const sentCache = { data: null };

function SentimentWidget({ wsData }) {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const [sentiment, setSentiment] = useState(sentCache.data);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const loadSentiment = useCallback(() => {
        setLoading(true);
        setError('');
        getSentiment('BNB')
            .then(result => { sentCache.data = result; setSentiment(result); })
            .catch(err => {
                const msg = err.response ? err.response.data : err.message;
                setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        // Fetch only the first time; afterwards keep the cached value across navigation.
        if (!sentCache.data) loadSentiment();
    }, [loadSentiment]);

    // Live WebSocket push (genuinely new data, not navigation) updates the cache too.
    useEffect(() => {
        if (wsData) { sentCache.data = wsData; setSentiment(wsData); }
    }, [wsData]);

    function getScoreColor(score) {
        if (score >= 50) return 'success';
        if (score >= 20) return 'info';
        if (score >= -20) return 'warning';
        if (score >= -50) return 'secondary';
        return 'danger';
    }

    function getSentimentLabel(sentimentEnum) {
        switch (sentimentEnum) {
            case 'VERY_POSITIVE': return t('dashboard.sentiment.veryPositive');
            case 'POSITIVE': return t('dashboard.sentiment.positive');
            case 'NEUTRAL': return t('dashboard.sentiment.neutral');
            case 'NEGATIVE': return t('dashboard.sentiment.negative');
            case 'VERY_NEGATIVE': return t('dashboard.sentiment.veryNegative');
            default: return sentimentEnum || 'N/A';
        }
    }

    function getGaugePosition(score) {
        // Map -100 to +100 -> 0% to 100%
        return Math.min(Math.max(((parseFloat(score) + 100) / 200) * 100, 0), 100);
    }

    const score = sentiment ? parseFloat(sentiment.score) : 0;

    return (
        <div className="col-12 col-lg-6 mb-4">
            <div className="card border-0 shadow">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        {t('dashboard.sentiment.title')}
                    </h5>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={loadSentiment}
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                        ) : (
                            <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                        )}
                    </button>
                </div>
                <div className="card-body">
                    {error && (
                        <div className="alert alert-warning py-2">
                            <small>{error}</small>
                        </div>
                    )}
                    {loading && !sentiment && (
                        <div className="text-center py-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted mt-2 mb-0">{t('dashboard.sentiment.loading')}</p>
                        </div>
                    )}
                    {!error && !sentiment && !loading && (
                        <p className="text-muted mb-0">{t('dashboard.sentiment.unavailable')}</p>
                    )}
                    {sentiment && (
                        <React.Fragment>
                            {/* Score and Sentiment Badge */}
                            <div className="text-center mb-3">
                                <span className={`badge bg-${getScoreColor(score)} fs-5 px-3 py-2`}>
                                    {score > 0 ? '+' : ''}{score}
                                </span>
                                <span className={`badge bg-${getScoreColor(score)} ms-2 fs-6 px-3 py-2`}>
                                    {getSentimentLabel(sentiment.sentiment)}
                                </span>
                            </div>

                            {/* Sentiment Gauge */}
                            <div className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <small className="text-danger">{t('dashboard.sentiment.bearish')}</small>
                                    <small className="text-muted">{t('dashboard.sentiment.neutral')}</small>
                                    <small className="text-success">{t('dashboard.sentiment.bullish')}</small>
                                </div>
                                <div className="position-relative" style={{ height: '10px', borderRadius: '5px', background: 'linear-gradient(to right, #dc3545, #ffc107, #198754)' }}>
                                    <div
                                        className="position-absolute bg-dark border border-white rounded-circle"
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            top: '-3px',
                                            left: `calc(${getGaugePosition(score)}% - 8px)`,
                                            transition: 'left 0.5s ease'
                                        }}
                                    ></div>
                                </div>
                            </div>

                            {/* Volume */}
                            {sentiment.volume > 0 && (
                                <div className="mb-3">
                                    <small className="text-muted">{t('dashboard.sentiment.mentions')} </small>
                                    <span className="fw-bold">{sentiment.volume.toLocaleString()}</span>
                                </div>
                            )}

                            {/* Top Keywords */}
                            {sentiment.topKeywords && sentiment.topKeywords.length > 0 && (
                                <div className="mb-3">
                                    <small className="text-muted d-block mb-1">{t('dashboard.sentiment.keywords')}</small>
                                    {(typeof sentiment.topKeywords === 'string' ? JSON.parse(sentiment.topKeywords) : sentiment.topKeywords).map((kw, i) => (
                                        <span key={i} className="badge bg-light text-dark me-1 mb-1">#{kw}</span>
                                    ))}
                                </div>
                            )}

                            {/* Top Posts */}
                            {sentiment.topPosts && (typeof sentiment.topPosts === 'string' ? JSON.parse(sentiment.topPosts) : sentiment.topPosts).length > 0 && (
                                <div className="mb-2">
                                    <small className="text-muted d-block mb-1">{t('dashboard.sentiment.posts')}</small>
                                    {(typeof sentiment.topPosts === 'string' ? JSON.parse(sentiment.topPosts) : sentiment.topPosts).slice(0, 3).map((post, i) => (
                                        <div key={i} className="border-start border-2 ps-2 mb-2" style={{ fontSize: '0.85em' }}>
                                            <span className={`badge bg-${post.sentiment === 'positive' ? 'success' : post.sentiment === 'negative' ? 'danger' : 'secondary'} me-1`} style={{ fontSize: '0.7em' }}>
                                                {post.sentiment}
                                            </span>
                                            {post.text}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Analyzed At */}
                            {sentiment.analyzedAt && (
                                <div className="text-end">
                                    <small className="text-muted">
                                        {t('dashboard.sentiment.analyzed')} {new Date(sentiment.analyzedAt).toLocaleTimeString()}
                                    </small>
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SentimentWidget;
